import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'
import type { Schema } from '@tiptap/pm/model'
import { createSyncedRows } from '../services/syncedRows'
import { useCorpusStore, type CorpusNode } from './corpus'
import { useSettingsStore } from './settings'
import {
  NOTE_MAX,
  isTokenId,
  scanLooseEnds,
  tokenSpecs,
  type FoundLooseEnd,
  type LooseEndShape,
  type TokenSpec,
} from '../utils/looseEnds'

// The Loose Ends page lists every loose end without opening every page, so
// each page keeps a small index row: its open loose ends plus a short history
// of resolved ones. One row per page, not per loose end, because the settings
// channel re-sends every row on every pull. History is capped for the same
// reason. Ideas captured away from the text sync as one row each.

const HISTORY_DAYS = 90
const HISTORY_PER_PAGE = 50
const HISTORY_NOTE_MAX = 140
const DAY_MS = 86_400_000
const BACKFILL_CONCURRENCY = 4

export interface IndexedLooseEnd {
  id: string
  shape: LooseEndShape
  note: string
  kind: string
  created: number
  before: string
  flagged: string
  after: string
}

export interface ResolvedLooseEnd {
  id: string
  shape: LooseEndShape
  note: string
  kind: string
  flagged: string
  /** The text a pin or token was replaced with, when filled in. */
  fill?: string
  resolvedAt: number
}

export interface PageLooseEnds {
  open: IndexedLooseEnd[]
  resolved: ResolvedLooseEnd[]
}

export interface LooseEndIdea {
  id: string
  text: string
  /** The page open when the idea was captured. */
  sourcePageId: string | null
  created: number
  doneAt: number | null
  /** The note the idea became, if it was made into one. */
  noteId: string | null
}

const SHAPES: LooseEndShape[] = ['pin', 'span', 'token']
const str = (value: unknown, max = 1000) => typeof value === 'string' ? value.slice(0, max) : ''
const time = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? value : 0

function parseOpen(raw: any): IndexedLooseEnd | null {
  if (!raw || typeof raw.id !== 'string' || !SHAPES.includes(raw.shape)) return null
  return {
    id: raw.id,
    shape: raw.shape,
    note: str(raw.note, NOTE_MAX),
    kind: str(raw.kind, 24),
    created: time(raw.created),
    before: str(raw.before, 200),
    flagged: str(raw.flagged, 200),
    after: str(raw.after, 200),
  }
}

function parseResolved(raw: any): ResolvedLooseEnd | null {
  if (!raw || typeof raw.id !== 'string' || !SHAPES.includes(raw.shape)) return null
  return {
    id: raw.id,
    shape: raw.shape,
    note: str(raw.note, HISTORY_NOTE_MAX),
    kind: str(raw.kind, 24),
    flagged: str(raw.flagged, 200),
    ...(typeof raw.fill === 'string' && raw.fill ? { fill: str(raw.fill, HISTORY_NOTE_MAX) } : {}),
    resolvedAt: time(raw.resolvedAt),
  }
}

function parsePage(raw: any): PageLooseEnds | null {
  if (!raw || !Array.isArray(raw.open) || !Array.isArray(raw.resolved)) return null
  return {
    open: raw.open.map(parseOpen).filter(Boolean) as IndexedLooseEnd[],
    resolved: raw.resolved.map(parseResolved).filter(Boolean) as ResolvedLooseEnd[],
  }
}

function parseIdea(raw: any): LooseEndIdea | null {
  if (!raw || typeof raw.id !== 'string' || typeof raw.text !== 'string') return null
  return {
    id: raw.id,
    text: raw.text.slice(0, 2000),
    sourcePageId: typeof raw.sourcePageId === 'string' ? raw.sourcePageId : null,
    created: time(raw.created),
    doneAt: typeof raw.doneAt === 'number' ? raw.doneAt : null,
    noteId: typeof raw.noteId === 'string' ? raw.noteId : null,
  }
}

function toIndexed(item: FoundLooseEnd): IndexedLooseEnd {
  return {
    id: item.id,
    shape: item.shape,
    note: item.note.slice(0, NOTE_MAX),
    kind: item.kind,
    created: item.created,
    before: item.before,
    flagged: item.flagged,
    after: item.after,
  }
}

function toResolved(item: IndexedLooseEnd, resolvedAt: number, fill?: string): ResolvedLooseEnd {
  return {
    id: item.id,
    shape: item.shape,
    note: item.note.slice(0, HISTORY_NOTE_MAX),
    kind: item.kind,
    flagged: item.flagged,
    ...(fill ? { fill: fill.slice(0, HISTORY_NOTE_MAX) } : {}),
    resolvedAt,
  }
}

/** Keeps history within its window and cap, newest first. */
/** How much history a page keeps: at most 90 days and 50 entries, or less as the writer chose. */
export interface HistoryLimits {
  days: number
  max: number
}

const DEFAULT_LIMITS: HistoryLimits = { days: HISTORY_DAYS, max: HISTORY_PER_PAGE }

export function pruneHistory(resolved: ResolvedLooseEnd[], now = Date.now(), limits: HistoryLimits = DEFAULT_LIMITS): ResolvedLooseEnd[] {
  const cutoff = now - Math.min(HISTORY_DAYS, limits.days) * DAY_MS
  return resolved
    .filter((entry) => entry.resolvedAt >= cutoff)
    .sort((a, b) => b.resolvedAt - a.resolvedAt)
    .slice(0, Math.min(HISTORY_PER_PAGE, limits.max))
}

type Outcome = { dismissed: true } | { dismissed?: false; fill?: string }

/**
 * The page's next index row after a scan. Loose ends that were open and are
 * gone now were resolved, unless the writer dismissed them.
 */
export function nextPageRow(
  prev: PageLooseEnds | undefined,
  found: FoundLooseEnd[],
  outcomes: Map<string, Outcome>,
  now = Date.now(),
  limits: HistoryLimits = DEFAULT_LIMITS,
): PageLooseEnds {
  const open = found.map(toIndexed)
  const stillOpen = new Set(open.map((item) => item.id))
  let resolved = prev?.resolved ?? []
  for (const old of prev?.open ?? []) {
    if (stillOpen.has(old.id) || isTokenId(old.id)) continue
    const outcome = outcomes.get(old.id)
    outcomes.delete(old.id)
    resolved = resolved.filter((entry) => entry.id !== old.id)
    if (outcome?.dismissed) continue
    resolved.unshift(toResolved(old, now, outcome?.fill))
  }
  // An undo brings a resolved loose end back; it is open again, not history.
  return { open, resolved: pruneHistory(resolved.filter((entry) => !stillOpen.has(entry.id)), now, limits) }
}

export const useLooseEndsStore = defineStore('looseEnds', () => {
  const corpus = useCorpusStore()
  const settings = useSettingsStore()
  /** History kept per page (Settings > Loose Ends). */
  const historyLimits = (): HistoryLimits => ({ days: settings.looseEnds.historyDays, max: settings.looseEnds.historyMax })

  const pageRows = createSyncedRows<PageLooseEnds>({
    storageKey: 'ink-loose-ends',
    remotePrefix: 'looseends.page.',
    parse: parsePage,
    pushDelayMs: 5_000,
  })
  const ideaRows = createSyncedRows<LooseEndIdea>({
    storageKey: 'ink-loose-end-ideas',
    remotePrefix: 'looseends.idea.',
    parse: parseIdea,
  })

  const pages = pageRows.values
  const ideas = computed(() => Object.values(ideaRows.values.value).sort((a, b) => b.created - a.created))
  const specs = computed<TokenSpec[]>(() => tokenSpecs(settings.looseEnds))

  // ── Page index ──────────────────────────────────────────────────────────

  const outcomes = new Map<string, Outcome>()

  /** Says how a loose end is about to leave its page, before the edit lands. */
  function expectOutcome(id: string, outcome: Outcome) {
    outcomes.set(id, outcome)
  }

  function recordScan(pageId: string, found: FoundLooseEnd[]) {
    const prev = pages.value[pageId]
    if (!prev && found.length === 0) return
    const next = nextPageRow(prev, found, outcomes, Date.now(), historyLimits())
    if (JSON.stringify(next) !== JSON.stringify(prev)) pageRows.write(pageId, next)
  }

  /** Tokens are plain text with no lasting id, so their history is written when the page resolves them. */
  function recordTokenResolved(pageId: string, item: IndexedLooseEnd, fill?: string) {
    const prev = pages.value[pageId] ?? { open: [], resolved: [] }
    pageRows.write(pageId, {
      open: prev.open.filter((entry) => entry.id !== item.id),
      resolved: pruneHistory([toResolved(item, Date.now(), fill), ...prev.resolved], Date.now(), historyLimits()),
    })
  }

  function clearHistory(pageIds: string[]) {
    for (const pageId of pageIds) {
      const row = pages.value[pageId]
      if (row?.resolved.length) pageRows.write(pageId, { open: row.open, resolved: [] })
    }
  }

  /** Every page, notes included, under a desk, folder, or page, in manuscript order; the whole corpus when null. */
  function pagesInScope(scopeId: string | null): CorpusNode[] {
    const root = scopeId ? corpus.findNode(scopeId) : null
    const pagesFound: CorpusNode[] = []
    const walk = (nodes: CorpusNode[]) => {
      for (const node of nodes) {
        if (node.subtype === 'page' && !node.isUnsaved) pagesFound.push(node)
        if (node.children?.length) walk(node.children)
      }
    }
    if (root) walk([root])
    else if (!scopeId) walk(corpus.tree)
    return pagesFound
  }

  // ── Reading pages never indexed ─────────────────────────────────────────

  const schema = shallowRef<Schema | null>(null)
  function setSchema(value: Schema | null) {
    schema.value = value
  }

  const attempted = new Set<string>()
  const scanning = shallowRef(false)

  async function scanPageFile(pageId: string, parseMarkdown: ((markdown: string) => any) | null): Promise<FoundLooseEnd[] | null> {
    const api = (window as any).api
    if (!api?.readPage || !schema.value) return null
    const res = await api.readPage(pageId)
    if (!res?.success || res.content === undefined) return null
    const json = res.format === 'markdown'
      ? parseMarkdown?.(res.content)
      : typeof res.content === 'string' ? JSON.parse(res.content) : res.content
    if (!json) return null
    return scanLooseEnds(schema.value.nodeFromJSON(json), specs.value)
  }

  /** Indexes pages that have no row yet, a few at a time, each at most once per session. */
  async function backfill(pageIds: string[], parseMarkdown: ((markdown: string) => any) | null) {
    const todo = pageIds.filter((id) => !pages.value[id] && !attempted.has(id) && id !== corpus.activePageId)
    if (todo.length === 0 || scanning.value) return
    scanning.value = true
    todo.forEach((id) => attempted.add(id))
    const next = async (): Promise<void> => {
      const id = todo.shift()
      if (!id) return
      try {
        const found = await scanPageFile(id, parseMarkdown)
        if (found && !pages.value[id]) recordScan(id, found)
      } catch (err) {
        console.warn('[LooseEnds] Could not read page', id, err)
      }
      return next()
    }
    try {
      await Promise.all(Array.from({ length: BACKFILL_CONCURRENCY }, next))
    } finally {
      scanning.value = false
    }
  }

  // ── Ideas ───────────────────────────────────────────────────────────────

  function addIdea(text: string, sourcePageId: string | null): LooseEndIdea | null {
    const trimmed = text.trim()
    if (!trimmed) return null
    const idea: LooseEndIdea = {
      id: `idea${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`,
      text: trimmed,
      sourcePageId,
      created: Date.now(),
      doneAt: null,
      noteId: null,
    }
    ideaRows.write(idea.id, idea)
    return idea
  }

  function updateIdea(id: string, patch: Partial<Omit<LooseEndIdea, 'id'>>) {
    const idea = ideaRows.values.value[id]
    if (idea) ideaRows.write(id, { ...idea, ...patch })
  }

  function deleteIdea(id: string) {
    if (ideaRows.values.value[id]) ideaRows.write(id, null)
  }

  /** Done ideas are history too, and leave with the same window. */
  function purgeOldIdeas(now = Date.now()) {
    const cutoff = now - historyLimits().days * DAY_MS
    for (const idea of ideas.value) if (idea.doneAt && idea.doneAt < cutoff) deleteIdea(idea.id)
  }

  return {
    pages,
    ideas,
    specs,
    expectOutcome,
    recordScan,
    recordTokenResolved,
    clearHistory,
    pagesInScope,
    setSchema,
    backfill,
    scanning,
    addIdea,
    updateIdea,
    deleteIdea,
    purgeOldIdeas,
    async syncWithRemote(remote: Parameters<typeof pageRows.syncWithRemote>[0]) {
      await Promise.all([pageRows.syncWithRemote(remote), ideaRows.syncWithRemote(remote)])
    },
  }
})
